var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { recoverPasswordAsync, resetPasswordAsync, } from '../../auth/redux/authSlice';
import { changeEmail, changePassword, exportAccountData, deleteAccount, resendVerificationEmail, verifyEmail, getAccountInformation, } from '../services/AccountService';
// ---------------------------
// Async Thunks
// ---------------------------
// Get Account Information
export const getAccountInformationAsync = createAsyncThunk('account/getAccountInformation', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const response = yield getAccountInformation();
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'Change email failed' });
    }
}));
// Change Email
export const changeEmailAsync = createAsyncThunk('account/changeEmail', (changeEmailRequest, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _b;
    try {
        const response = yield changeEmail(changeEmailRequest);
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_b = error.response) === null || _b === void 0 ? void 0 : _b.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'Change email failed' });
    }
}));
// Verify email
export const verifyEmailAsync = createAsyncThunk('account/verifyEmail', (token, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    try {
        const response = yield verifyEmail(token);
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_c = error.response) === null || _c === void 0 ? void 0 : _c.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'Email verification failed' });
    }
}));
// Re-send verification Email
export const resendVerificationEmailAsync = createAsyncThunk('account/resendVerificationEmail', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _d;
    try {
        const response = yield resendVerificationEmail();
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_d = error.response) === null || _d === void 0 ? void 0 : _d.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({
            message: 'Re-send verification email failed',
        });
    }
}));
// Change Password
export const changePasswordAsync = createAsyncThunk('account/changePassword', (changePasswordRequest, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _e;
    try {
        const response = yield changePassword(changePasswordRequest);
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_e = error.response) === null || _e === void 0 ? void 0 : _e.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'Change password failed' });
    }
}));
// Download User Data
export const exportAccountDataAsync = createAsyncThunk('account/exportAccountData', (exportAccountDataRequest, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _f;
    try {
        const response = yield exportAccountData(exportAccountDataRequest);
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_f = error.response) === null || _f === void 0 ? void 0 : _f.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'Download account data failed' });
    }
}));
// Delete Account
export const deleteAccountAsync = createAsyncThunk('account/deleteAccount', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _g, _h;
    try {
        const response = yield deleteAccount();
        return response;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        if ((_h = (_g = error.response) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.message) {
            return rejectWithValue({ message: error.response.data.message });
        }
        return rejectWithValue({ message: 'Delete account failed' });
    }
}));
// ---------------------------
// Initial State
// ---------------------------
export const accountInitialState = {
    status: {
        getAccountInformation: 'idle',
        changeEmail: 'idle',
        changePassword: 'idle',
        downloadData: 'idle',
        deleteAccount: 'idle',
    },
    error: {
        getAccountInformation: null,
        changeEmail: null,
        changePassword: null,
        downloadData: null,
        deleteAccount: null,
    },
};
// ---------------------------
// Account Slice
// ---------------------------
const accountSlice = createSlice({
    name: 'account',
    initialState: accountInitialState,
    reducers: {},
    extraReducers: (builder) => {
        // ---------------------------
        // Get Account Information
        // ---------------------------
        builder
            .addCase(getAccountInformationAsync.pending, (state) => {
            state.status.getAccountInformation = 'loading';
            state.error.getAccountInformation = null;
        })
            .addCase(getAccountInformationAsync.fulfilled, (state) => {
            state.status.getAccountInformation = 'succeeded';
        })
            .addCase(getAccountInformationAsync.rejected, (state, action) => {
            var _a;
            state.status.getAccountInformation = 'failed';
            state.error.getAccountInformation =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Change email failed';
        });
        // ---------------------------
        // Change Email
        // ---------------------------
        builder
            .addCase(changeEmailAsync.pending, (state) => {
            state.status.changeEmail = 'loading';
            state.error.changeEmail = null;
        })
            .addCase(changeEmailAsync.fulfilled, (state, action) => {
            state.status.changeEmail = 'succeeded';
        })
            .addCase(changeEmailAsync.rejected, (state, action) => {
            var _a;
            state.status.changeEmail = 'failed';
            state.error.changeEmail =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Change email failed';
        })
            // ---------------------------
            // Verify Email
            // ---------------------------
            .addCase(verifyEmailAsync.pending, (state) => {
            state.status.changeEmail = 'loading';
            state.error.changeEmail = null;
        })
            .addCase(verifyEmailAsync.fulfilled, (state) => {
            state.status.changeEmail = 'succeeded';
        })
            .addCase(verifyEmailAsync.rejected, (state, action) => {
            var _a;
            state.status.changeEmail = 'failed';
            state.error.changeEmail =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Verify email failed';
        })
            // ---------------------------
            // Resend Verification Email
            // ---------------------------
            .addCase(resendVerificationEmailAsync.pending, (state) => {
            state.status.changeEmail = 'loading';
            state.error.changeEmail = null;
        })
            .addCase(resendVerificationEmailAsync.fulfilled, (state) => {
            state.status.changeEmail = 'succeeded';
        })
            .addCase(resendVerificationEmailAsync.rejected, (state, action) => {
            var _a;
            state.status.changeEmail = 'failed';
            state.error.changeEmail =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) ||
                    'Resend verification email failed';
        })
            // ---------------------------
            // Change Password
            // ---------------------------
            .addCase(changePasswordAsync.pending, (state) => {
            state.status.changePassword = 'loading';
            state.error.changePassword = null;
        })
            .addCase(changePasswordAsync.fulfilled, (state) => {
            state.status.changePassword = 'succeeded';
        })
            .addCase(changePasswordAsync.rejected, (state, action) => {
            var _a;
            state.status.changePassword = 'failed';
            state.error.changePassword =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Change password failed';
        })
            // ---------------------------
            // Download User Data
            // ---------------------------
            .addCase(exportAccountDataAsync.pending, (state) => {
            state.status.downloadData = 'loading';
            state.error.downloadData = null;
        })
            .addCase(exportAccountDataAsync.fulfilled, (state) => {
            state.status.downloadData = 'succeeded';
        })
            .addCase(exportAccountDataAsync.rejected, (state, action) => {
            var _a;
            state.status.downloadData = 'failed';
            state.error.downloadData =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Download user data failed';
        })
            // ---------------------------
            // Delete Account
            // ---------------------------
            .addCase(deleteAccountAsync.pending, (state) => {
            state.status.deleteAccount = 'loading';
            state.error.deleteAccount = null;
        })
            .addCase(deleteAccountAsync.fulfilled, (state) => {
            state.status.deleteAccount = 'succeeded';
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        })
            .addCase(deleteAccountAsync.rejected, (state, action) => {
            var _a;
            state.status.deleteAccount = 'failed';
            state.error.deleteAccount =
                ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.message) || 'Delete account failed';
        })
            // ---------------------------
            // Authentication
            // ---------------------------
            .addCase(recoverPasswordAsync.fulfilled, (state) => {
            state.status.changePassword = 'succeeded';
        })
            .addCase(resetPasswordAsync.fulfilled, (state) => {
            state.status.changePassword = 'succeeded';
        });
    },
});
// ---------------------------
// Export Actions and Reducer
// ---------------------------
export const accountReducer = accountSlice.reducer;
export default accountSlice;
